// GTIN-register (admin mockup §3): the own prefix's item references — assigned, free, next —
// and allocation of the next number to an article with the check digit computed.

import { useEffect, useMemo, useState } from "react";
import { gs1CheckDigit } from "@trail/core";
import { Admin, Api, type BundleProduct, type GtinRegister } from "../api/client.js";
import { t } from "../i18n.js";

export function GtinRegisterView() {
  const [reg, setReg] = useState<GtinRegister | null>(null);
  const [products, setProducts] = useState<BundleProduct[]>([]);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<BundleProduct | null>(null);
  const [level, setLevel] = useState<"each" | "inner" | "case" | "pallet">("case");
  // The indicator digit of the GTIN-14 to allocate (owner 2026-10-29: 9 = variable measure in
  // GS1-128 is the rule at Kalundgren, 0 = a plain GTIN-13 for consumer packs).
  const [indicator, setIndicator] = useState(9);
  const nextGtin14 = reg?.prefix && reg.nextRef ? (() => { const body = `${indicator}${reg.prefix}${reg.nextRef}`; return body + gs1CheckDigit(body); })() : null;
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  // The prefix itself (owner 2026-10-29: "where do I set the GS1 prefix?" — it was script-only).
  const [editPrefix, setEditPrefix] = useState(false);
  const [prefixInput, setPrefixInput] = useState("");
  const savePrefix = async () => {
    setError(null);
    try {
      await Admin.setGs1Prefix(prefixInput.trim());
      setMsg(t("adm.gt.prefix.saved", { prefix: prefixInput.trim() }));
      setEditPrefix(false);
      setPrefixInput("");
      void reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const reload = () => Admin.gtinRegister().then(setReg).catch(() => setReg(null));
  useEffect(() => {
    void reload();
    Api.bundle().then((b) => setProducts(b.products)).catch(() => undefined);
  }, []);

  const candidates = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? products.filter((p) => p.sku.toLowerCase().includes(needle) || p.name.toLowerCase().includes(needle)).slice(0, 8) : [];
  }, [products, q]);

  const allocate = async () => {
    if (!pick) return;
    setError(null);
    try {
      const r = await Admin.allocateGtin(pick.id, level, indicator);
      setMsg(t("adm.gt.done", { gtin: r.gtin.replace(/^0/, ""), sku: pick.sku }));
      setPick(null);
      setQ("");
      void reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  type Row = { gtin: string; ref: string; sku: string | null; name: string | null; level: string | null; source: string | null; status: "active" | "inactive" | "reserved" };
  const all: Row[] = [
    ...(reg?.assigned ?? []).map((a): Row => ({ gtin: a.gtin, ref: a.ref, sku: a.sku, name: a.name, level: a.level, source: a.source, status: a.active ? "active" : "inactive" })),
    ...(reg?.reserved ?? []).map((r): Row => ({ gtin: r.gtin, ref: r.ref, sku: r.sku, name: r.note, level: null, source: r.source, status: "reserved" })),
  ].sort((x, y) => x.ref.localeCompare(y.ref));
  const needle = filter.trim().toLowerCase();
  const rows = all.filter((a) => !needle || a.ref.includes(needle) || a.gtin.includes(needle) || (a.sku ?? "").toLowerCase().includes(needle) || (a.name ?? "").toLowerCase().includes(needle));
  const inactive = (reg?.assigned ?? []).filter((a) => !a.active).length;

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.gtins")}</h1><div className="crumb">{reg?.prefix ? t("adm.gt.sub", { prefix: reg.prefix, n: reg.capacity.toLocaleString("sv-SE") }) : ""}</div></div>
        {reg?.prefix && !editPrefix && <button type="button" className="abtn sm" onClick={() => { setPrefixInput(reg.prefix ?? ""); setEditPrefix(true); }}>{t("adm.gt.prefix.change")}</button>}
      </div>
      <div className="adm-content">
        {msg && <p className="ahint" style={{ color: "var(--ok)" }}>{msg}</p>}
        {error && <p className="ahint" style={{ color: "var(--bad)" }}>{error}</p>}
        {reg && !reg.prefix && <p className="ahint" style={{ color: "var(--warn)" }}>{t("adm.gt.noPrefix")}</p>}
        {reg && (!reg.prefix || editPrefix) && (
          <form className="card" style={{ maxWidth: 520 }} onSubmit={(e) => { e.preventDefault(); void savePrefix(); }}>
            <div className="aform">
              <label><span>{t("adm.gt.prefix")}</span><input className="ain mono" inputMode="numeric" pattern="[0-9]{4,12}" value={prefixInput} onChange={(e) => setPrefixInput(e.target.value.replace(/\D/g, ""))} placeholder="735003415" autoFocus /></label>
            </div>
            {reg.prefix && <p className="ahint">{t("adm.gt.prefix.warn")}</p>}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button type="submit" className="abtn primary" disabled={!/^\d{4,12}$/.test(prefixInput)}>{t("adm.gt.prefix.save")}</button>
              {reg.prefix && <button type="button" className="abtn" onClick={() => setEditPrefix(false)}>{t("common.cancel")}</button>}
            </div>
          </form>
        )}
        {reg?.prefix && (
          <>
            <div className="tiles">
              <div className="tile"><div className="k">{t("adm.gt.capacity")}</div><div className="v">{reg.capacity.toLocaleString("sv-SE")}</div><div className="d">{t("adm.gt.capacity.d")}</div></div>
              <div className="tile"><div className="k">{t("adm.gt.assigned")}</div><div className="v">{reg.usedRefs.toLocaleString("sv-SE")}</div><div className="d">{t("adm.gt.assigned.d2", { codes: reg.assigned.length, n: inactive })}</div></div>
              <div className="tile"><div className="k">{t("adm.gt.reserved")}</div><div className="v">{reg.reserved.length.toLocaleString("sv-SE")}</div><div className="d">{t("adm.gt.reserved.d")}</div></div>
              <div className="tile ok"><div className="k">{t("adm.gt.free")}</div><div className="v">{reg.free.toLocaleString("sv-SE")}</div><div className="d">{reg.nextRef ? t("adm.gt.free.d", { ref: reg.nextRef }) : "—"}</div></div>
            </div>
            <div className="adm-cols">
              <div className="card adm-grow">
                <div className="hd">{t("adm.gt.list", { n: all.length })} <span className="sub"><input className="ain" style={{ height: 28, width: 200, borderRadius: 8, border: "1px solid var(--line)", background: "var(--bg)", padding: "0 8px", font: "inherit", color: "var(--text)" }} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("adm.gt.search")} /></span></div>
                <div className="bd flush">
                  <table className="at">
                    <thead><tr><th>{t("adm.gt.gtin")}</th><th>{t("adm.gt.ref")}</th><th>{t("adm.gt.product")}</th><th>{t("adm.gt.level")}</th><th>{t("adm.gt.source")}</th><th>{t("adm.col.status")}</th></tr></thead>
                    <tbody>
                      {rows.slice(0, 300).map((a) => (
                        <tr key={a.gtin}><td className="id">{a.gtin.replace(/^0/, "")}</td><td className="id">{a.ref}</td><td>{a.name ?? "—"}{a.sku ? <span className="mut"> {a.sku}</span> : null}</td><td className="mut">{a.level ?? "—"}</td><td className="mut">{a.source ?? "—"}</td><td>{a.status === "active" ? <span className="pill ok">{t("adm.gt.inUse")}</span> : a.status === "reserved" ? <span className="pill warn">{t("adm.gt.reservedOne")}</span> : <span className="pill mut">{t("adm.gt.inactive")}</span>}</td></tr>
                      ))}
                      {rows.length > 300 && <tr><td colSpan={6} className="mut">{t("rows.more", { n: rows.length - 300 })}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card adm-w380">
                <div className="hd">{t("adm.gt.allocateTo")}</div>
                <div className="bd aform" style={{ gridTemplateColumns: "120px 1fr" }}>
                  <span>{t("adm.gt.product")}</span>
                  <div>
                    <input className="ain" value={pick ? `${pick.sku} · ${pick.name}` : q} onChange={(e) => { setPick(null); setQ(e.target.value); }} placeholder={t("adm.gt.search")} />
                    {!pick && candidates.length > 0 && <ul className="alist" style={{ marginTop: 6 }}>{candidates.map((p) => <li key={p.id}><button type="button" className="abtn sm" style={{ width: "100%", justifyContent: "flex-start" }} onClick={() => setPick(p)}>{p.sku} · {p.name}</button></li>)}</ul>}
                  </div>
                  <span>{t("adm.gt.level")}</span>
                  <div className="achips">{(["case", "each", "inner", "pallet"] as const).map((l) => <button key={l} type="button" className={`achip${level === l ? " on" : ""}`} onClick={() => setLevel(l)}>{l === "case" ? t("unit.case") : l === "each" ? t("unit.each") : l === "inner" ? t("unit.inner") : t("unit.pallet")}</button>)}</div>
                  <span>{t("adm.gt.indicator")}</span>
                  <div className="achips">
                    {[9, 0].map((i) => <button key={i} type="button" className={`achip${indicator === i ? " on" : ""}`} onClick={() => setIndicator(i)}>{t(i === 9 ? "adm.gt.indicator.9" : "adm.gt.indicator.0")}</button>)}
                    <select className="ain" style={{ width: "auto", height: 28 }} value={indicator === 9 || indicator === 0 ? "" : String(indicator)} onChange={(e) => { if (e.target.value) setIndicator(Number(e.target.value)); }}>
                      <option value="">{indicator !== 9 && indicator !== 0 ? t("adm.gt.indicator.n", { n: indicator }) : "1–8 …"}</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <option key={i} value={i}>{t("adm.gt.indicator.n", { n: i })}</option>)}
                    </select>
                  </div>
                  <span>{t("adm.gt.gtin")}</span><div className="ain mono" style={{ display: "flex", alignItems: "center" }}>{nextGtin14 ? (indicator === 0 ? nextGtin14.slice(1) : nextGtin14) : "—"}</div>
                  <span>{t("adm.gt.check")}</span><div className="ain mono" style={{ display: "flex", alignItems: "center" }}>{nextGtin14 ? `${nextGtin14.slice(-1)} · ${t("adm.gt.computed")}` : "—"}</div>
                  <span></span><button type="button" className="abtn primary" disabled={!pick || !nextGtin14} onClick={() => void allocate()}>{t("adm.gt.do")}</button>
                </div>
                <div className="bd"><p className="ahint">{t("adm.gt.noReuse")}</p></div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
